import { httpService } from "./http.service";

const PM_BASE = "/api/pm/v1";

export interface DenialRate {
  insurer: string;
  cptCode: string;
  totalClaims: number;
  deniedClaims: number;
  denialRate: number;
}

export interface DenialRateResult {
  rows: DenialRate[];
  totalClaims: number;
  totalDenied: number;
}

export interface Patient {
  patientId: string;
  firstName: string;
  lastName: string;
  dateOfService: string;
  lastVisit: string;
  insurer: string;
  diagnoses: string[];
  cptCodes: string[];
}

export interface PatientSearchResult {
  patients: Patient[];
  total: number;
  page: number;
  totalPages: number;
}

export type PayerPriority = "all" | "primary" | "secondary" | "primary_secondary" | "tertiary";

export interface PatientSearchBody {
  insurers?: string[];
  diagnoses?: string[];
  cptCodes?: string[];
  dateFrom?: string;
  dateTo?: string;
  priority?: PayerPriority;
  page?: number;
  limit?: number;
}

export interface InsurerCount {
  insurer: string;
  count: number;
}

export interface PatientDistribution {
  distribution: InsurerCount[];
}

export interface PatientDistributionBody {
  insurers?: string[];
  diagnoses?: string[];
  cptCodes?: string[];
  dateFrom?: string;
  dateTo?: string;
  priority?: PayerPriority;
}

export interface MonthlyDenialRate {
  month: number;
  totalClaims: number;
  deniedClaims: number;
  denialRate: number;
}

export interface DenialKPIResult {
  months: MonthlyDenialRate[];
}

export interface ClaimComment {
  id: string;
  entityType: string;
  entityId: string;
  authorId: string;
  authorEmail: string;
  body: string;
  createdAt: string;
}

export interface ClaimStatusRow {
  patientId: string;
  firstName: string;
  lastName: string;
  encounterId: string;
  ehrClaimId: string;
  insurer: string;
  cptCode: string;
  status: string;
  dateOfService: string;
  lastVisit: string;
  totalCharge: number;
  outstanding: number;
  serviceLineId: string;
  reviewed: boolean;
  reviewedAt?: string;
  reviewedBy?: string;
  comments: ClaimComment[];
}

export interface ClaimStatusResult {
  rows: ClaimStatusRow[];
  total: number;
  uniqueClaims: number;
  page: number;
  totalPages: number;
}

export interface ClaimStatusBody {
  insurers?: string[];
  cptCodes?: string[];
  statuses?: string[];
  dateFrom?: string;
  dateTo?: string;
  priority?: "Primary" | "Secondary";
  search?: string;
  searchBy?: string[];
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  page?: number;
  limit?: number;
}

const pmService = {
  getDenialsByInsurerCpt(
    dateFrom: string,
    dateTo: string,
    groupBy: "insurer" | "cpt",
    priority: PayerPriority = "primary"
  ): Promise<DenialRateResult> {
    return httpService.get<DenialRateResult>(
      `${PM_BASE}/denials/by-insurer-cpt?date_from=${dateFrom}&date_to=${dateTo}&group_by=${groupBy}&priority=${priority}`
    );
  },

  getDenialKPI(
    year: number,
    ytd: boolean,
    priority: PayerPriority = "primary"
  ): Promise<DenialKPIResult> {
    return httpService.get<DenialKPIResult>(
      `${PM_BASE}/kpi/denial-monthly?year=${year}&ytd=${ytd}&priority=${priority}`
    );
  },

  searchPatients(body: PatientSearchBody): Promise<PatientSearchResult> {
    return httpService.post<PatientSearchResult>(`${PM_BASE}/patients/search`, body);
  },

  getPatientDistribution(body: PatientDistributionBody): Promise<PatientDistribution> {
    return httpService.post<PatientDistribution>(`${PM_BASE}/patients/distribution`, body);
  },

  getInsurers(): Promise<{ insurers: string[] }> {
    return httpService.get<{ insurers: string[] }>(`${PM_BASE}/filters/insurers`);
  },

  getDiagnoses(): Promise<{ diagnoses: string[] }> {
    return httpService.get<{ diagnoses: string[] }>(`${PM_BASE}/filters/diagnoses`);
  },

  getCptCodes(): Promise<{ cptCodes: string[] }> {
    return httpService.get<{ cptCodes: string[] }>(`${PM_BASE}/filters/cpt-codes`);
  },

  searchClaims(body: ClaimStatusBody): Promise<ClaimStatusResult> {
    return httpService.post<ClaimStatusResult>(`${PM_BASE}/claims/patients`, body);
  },

  setReview(
    serviceLineId: string,
    reviewed: boolean
  ): Promise<{ entityType: string; entityId: string; reviewed: boolean }> {
    const url = `${PM_BASE}/reviews/service_line/${encodeURIComponent(serviceLineId)}`;
    return reviewed ? httpService.put(url, {}) : httpService.delete(url);
  },

  bulkReview(
    serviceLineIds: string[],
    reviewed: boolean
  ): Promise<{ entityType: string; reviewed: boolean; requested: number; affected: number }> {
    return httpService.post(`${PM_BASE}/reviews/service_line/bulk`, {
      ids: serviceLineIds,
      reviewed,
    });
  },

  addComment(entityType: string, entityId: string, body: string): Promise<ClaimComment> {
    return httpService.post<ClaimComment>(
      `${PM_BASE}/reviews/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}/comments`,
      { body }
    );
  },

  deleteComment(entityType: string, entityId: string, commentId: string): Promise<void> {
    return httpService.delete(
      `${PM_BASE}/reviews/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}/comments/${encodeURIComponent(commentId)}`
    );
  },

  async downloadClaimsXLSX(params: Omit<ClaimStatusBody, "page" | "limit">): Promise<void> {
    const q = new URLSearchParams();
    params.insurers?.forEach((v) => q.append("insurers", v));
    params.cptCodes?.forEach((v) => q.append("cptCodes", v));
    params.statuses?.forEach((v) => q.append("statuses", v));
    if (params.dateFrom) q.set("dateFrom", params.dateFrom);
    if (params.dateTo) q.set("dateTo", params.dateTo);
    if (params.priority) q.set("priority", params.priority);
    if (params.search) q.set("search", params.search);
    params.searchBy?.forEach((v) => q.append("searchBy", v));
    if (params.sortBy) q.set("sortBy", params.sortBy);
    if (params.sortOrder) q.set("sortOrder", params.sortOrder);

    const token = localStorage.getItem("authToken");
    const res = await fetch(`${PM_BASE}/claims/patients.xlsx?${q.toString()}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) {
      if (res.status === 413) throw new Error("too_large");
      throw new Error("download_failed");
    }
    const blob = await res.blob();
    const objUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objUrl;
    const cd = res.headers.get("content-disposition");
    const match = cd?.match(/filename="([^"]+)"/);
    a.download = match?.[1] ?? "claim-status.xlsx";
    a.click();
    URL.revokeObjectURL(objUrl);
  },
};

export { pmService };
